import React, { useState } from 'react';

import { useFormik } from 'formik';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { changeProfile } from 'api/endpoints/user';
import Button from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import Input from 'components/Input';
import { ENUM_ROLE_USER } from 'constants/enum';
import { MESSAGE_COMMON, MESSAGE_PROFILE } from 'constants/string/messages';
import { TEXT_COMMON, TEXT_PROFILE } from 'constants/string/texts';
import useUser from 'hooks/useUser';
import { toastError, toastSuccess } from 'lib/toast';
import { setLoading } from 'store/actions/common';
import { getCodeJapaneseGovernmentsWith, getJapaneseGovernments } from 'utils/helper';

const UpdateProfile = ({ back }) => {
  const dispatch = useDispatch();
  const { getMe } = useUser();
  const japanese_government = getJapaneseGovernments() || [];
  const { me } = useSelector((state) => state.auth);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const initialValues = {
    municipality: me.municipality || '',
    department: me?.department || '',
  };

  const handleUpdateProfile = async (values) => {
    try {
      dispatch(setLoading({ isOpen: true }));
      await changeProfile({
        ...values,
        municipality: getCodeJapaneseGovernmentsWith(values.municipality, true) || '',
      })
        .then(async (response) => {
          toastSuccess(response.message || '');
          getMe();
          back();
        })
        .catch((errors) => {
          toastError(errors.message);
        });
      dispatch(setLoading({ isOpen: false }));
    } catch {
      //  Handle error
      dispatch(setLoading({ isOpen: false }));
    }
  };

  const form = useFormik({
    initialValues: initialValues,
    validateOnChange: isSubmitted,
    validateOnBlur: false,
    validate: (values) => {
      const errors = {};
      if (!values.municipality) {
        errors.municipality = true;
      } else {
        const japanese_government = getJapaneseGovernments() || [];
        const governmentExits = _.findIndex(japanese_government, (item) => _.some(item.options, { value: values.municipality }));
        if (governmentExits === -1) {
          errors.municipality = true;
        }
      }
      if (!values.department) errors.department = true;
      setIsSubmitted(true);
      return errors;
    },
    onSubmit: async (values) => {
      await handleUpdateProfile(values);
    },
  });

  return (
    <>
      <div className="flex-between-center px-6 py-4 border-b border-white-720">
        <p className="font-bold">
          {me?.role === ENUM_ROLE_USER.ADMIN ? TEXT_PROFILE.TITLE_PROFILE_EDITING : TEXT_PROFILE.TITLE_PROFILE_EDITING_USER}
        </p>
      </div>

      <div className="p-6">
        {me?.role === ENUM_ROLE_USER.ADMIN && (
          <fieldset className="mb-6">
            <p className="text-black/85 mb-2 flex items-center">
              <span className="text-danger-411 mr-1">
                <img src={require('assets/resources/Required.svg').default} alt="required" width={7} height={7} />
              </span>
              {TEXT_PROFILE.MUNICIPALITY}
            </p>
            <Input
              onSelect={(e) => {
                form.setFieldValue('municipality', e);
              }}
              value={form.values.municipality}
              onChange={(e) => {
                form.setFieldValue('municipality', e);
              }}
              name="municipality"
              classNameInput="!h-10"
              className="w-full"
              type="autocomplete"
              options={japanese_government}
              filterOption={(inputValue, option) =>
                option.value &&
                (option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1 ||
                  option.code.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1)
              }
              invalid={form.errors?.municipality}
            />
            <p className={`text-sm ${form.errors.municipality ? 'text-danger-229 h-5.75' : 'text-secondary-354'}`}>
              {form.values.municipality && form.errors.municipality && <ErrorMessage>{MESSAGE_PROFILE.GOVERNMENT_INCORRECT}</ErrorMessage>}
              {!form.values.municipality && form.errors?.municipality && <ErrorMessage>{MESSAGE_COMMON.NOT_EMPTY}</ErrorMessage>}
            </p>
            {!form.errors.municipality && <p className="text-sm text-secondary-354 h-5.75">{TEXT_PROFILE.MUNICIPALITY_REQUIRED}</p>}
          </fieldset>
        )}

        <fieldset>
          <p className="text-black/85 mb-2 flex items-center">
            <span className="text-danger-411 mr-1">
              <img src={require('assets/resources/Required.svg').default} alt="required" width={7} height={7} />
            </span>
            {TEXT_PROFILE.DEPARTMENT}
          </p>
          <Input
            id="department"
            type="text"
            name="department"
            value={form.values.department}
            onChange={form.handleChange}
            className="w-full"
            invalid={form.errors?.department}
          />
          <p className={`text-sm ${form.errors.department ? 'text-danger-229 h-5.75' : 'text-secondary-354'}`}>
            {form.errors?.department ? (
              <ErrorMessage>{MESSAGE_COMMON.NOT_EMPTY}</ErrorMessage>
            ) : (
              <p className="text-sm text-secondary-354 h-5.75">{TEXT_PROFILE.DEPARTMENT_REQUIRED}</p>
            )}
          </p>
        </fieldset>
      </div>

      <div className="h-16 border-t border-white-720 flex-end-center gap-2 py-4 px-6">
        <Button className="h-8 text-sm" text={TEXT_COMMON.CANCEL_TEXT} color="outline" onClick={back} />
        <Button className="h-8 text-sm" text={TEXT_COMMON.RENEW} color="primary" onClick={form.handleSubmit} />
      </div>
    </>
  );
};

export default React.memo(UpdateProfile);
